import { config } from '@app/config';
import { theme } from '@mi-front/ui';

/** Converts a `#rrggbb` theme color to Google Static Maps `0xRRGGBB` format. */
function toStaticMapColor(hex: string): string {
  return `0x${hex.replace('#', '').toUpperCase()}`;
}

const ONLINE_MARKER_COLOR = toStaticMapColor(theme.blueNewPastel);
const OFFLINE_MARKER_COLOR = toStaticMapColor(theme.pinkPastel);

export interface BuildGoogleStaticMapUrlParams {
  lat: string;
  lng: string;
  isOnline: boolean;
  /** When true, applies Static Maps `invert_lightness` for a dark map. */
  isDark?: boolean;
  /** Logical pixel width (before `scale`). Defaults to 640. */
  width?: number;
  /** Logical pixel height (before `scale`). Defaults to 200. */
  height?: number;
  zoom?: number;
}

/**
 * Builds a Google Maps Static API URL for a device card map thumbnail.
 * Returns null when the API key or coordinates are missing.
 */
export function buildGoogleStaticMapUrl({
  lat,
  lng,
  isOnline,
  isDark = false,
  width = 640,
  height = 200,
  zoom = 14,
}: BuildGoogleStaticMapUrlParams): string | null {
  const key = config.googleMapsStaticApiKey;

  if (!key || !lat || !lng) {
    return null;
  }

  const markerColor = isOnline ? ONLINE_MARKER_COLOR : OFFLINE_MARKER_COLOR;
  const params = new URLSearchParams({
    center: `${lat},${lng}`,
    zoom: String(zoom),
    size: `${width}x${height}`,
    scale: '2',
    maptype: 'roadmap',
    markers: `color:${markerColor}|${lat},${lng}`,
    key,
  });

  // Hide POIs and transit so the card thumb stays clean.
  params.append('style', 'feature:poi|visibility:off');
  params.append('style', 'feature:transit|visibility:off');

  if (isDark) {
    params.append('style', 'invert_lightness:true');
  }

  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}
